import { createOpenAICompatible } from '@ai-sdk/openai-compatible';
import {
  convertDataContentToBase64String,
  type LanguageModelMiddleware,
  wrapLanguageModel,
} from 'ai';
import { createAuthorizationHeaderValue } from 'lib-shared-auth';
import { config } from 'lib-shared-backend-config';

const CHAT_COMPLETION_API = 'v4/ai/model';

const SPREADSHEET_MEDIA_TYPE =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

type CallOptions = Parameters<
  NonNullable<LanguageModelMiddleware['transformParams']>
>[0]['params'];
type UserContentPart = Extract<
  CallOptions['prompt'][number],
  { role: 'user' }
>['content'][number];

const gateway = createOpenAICompatible({
  name: 'dixa-gateway',
  baseURL: `${config.url.rest}/${CHAT_COMPLETION_API}`,
  fetch: async (input, init) => {
    const authorization = await createAuthorizationHeaderValue();
    const headers = new Headers(init?.headers);
    headers.set('Authorization', authorization);
    return fetch(input, { ...init, headers });
  },
});

const isGatewayDocument = (mediaType: string): boolean =>
  mediaType.startsWith('text/') || mediaType === SPREADSHEET_MEDIA_TYPE;

/**
 * The provider flattens text files into plain text and rejects spreadsheets,
 * but spreads a part's provider options over the request part. That lets a
 * text part carry an OpenAI `file` part, which the gateway reads as a document.
 */
const toGatewayDocument = (part: UserContentPart): UserContentPart => {
  if (part.type !== 'file' || part.data.type !== 'data') return part;
  if (!isGatewayDocument(part.mediaType)) return part;

  const base64 = convertDataContentToBase64String(part.data.data);

  return {
    type: 'text',
    text: '',
    providerOptions: {
      openaiCompatible: {
        type: 'file',
        file: {
          filename: part.filename ?? 'attachment',
          file_data: `data:${part.mediaType};base64,${base64}`,
        },
      },
    },
  };
};

const gatewayDocumentsMiddleware: LanguageModelMiddleware = {
  transformParams: async ({ params }) => ({
    ...params,
    prompt: params.prompt.map((message) =>
      message.role === 'user'
        ? { ...message, content: message.content.map(toGatewayDocument) }
        : message,
    ),
  }),
};

/**
 * OpenAI-compatible model that targets our gateway. See
 * https://ai-sdk.dev/providers/openai-compatible-providers for more details.
 */
export const dixaGateway = (
  modelId: string,
): ReturnType<typeof wrapLanguageModel> =>
  wrapLanguageModel({
    model: gateway(modelId),
    middleware: gatewayDocumentsMiddleware,
  });
